/**
 * Why a HELD scheduled order did not fire at its `fireAt`.
 *
 * Written by the server's scheduled-order activator (`fireHeldOrder`, via
 * `heldOrderRequiredChoices` in `@order-up/core`) on a TILL order whose
 * pending lines still owe a size or a required modifier choice — the till's
 * own send rule (`required_modifier_gate.dart`), ported to
 * `@order-up/common` `linesOwingRequiredChoices`. The order stays `scheduled`;
 * the activator re-judges it every minute and fires it the moment the lines
 * are finished, clearing this field. See
 * docs/UNIFIED_ORDER_ACCEPTANCE_ARCHITECTURE.md §6b.
 *
 * SERVER-OWNED and DERIVED: it is a cache of the last verdict, never an
 * input. A till write-back that drops it costs one re-judgement, not a
 * wrong fire — the activator decides from the lines every time.
 */

/** The one reason today. A string union so a second reason is additive. */
export type HeldOrderFireBlockReason = "required_choices_owed";

export const HELD_ORDER_FIRE_BLOCK_REQUIRED_CHOICES: HeldOrderFireBlockReason =
  "required_choices_owed";

/** One line that held the order back. */
export interface IHeldOrderFireBlockLine {
  readonly orderItemGuid?: string;
  readonly name: string;
  /** A size-priced line with no size chosen. */
  readonly owesSize: boolean;
  /** The required modifier groups it has not answered, in panel order. */
  readonly unansweredGroups: ReadonlyArray<{
    readonly modifierGroupId: number;
    readonly name: string;
    readonly minSelection: number;
  }>;
}

export interface IHeldOrderFireBlock {
  readonly reason: HeldOrderFireBlockReason;
  readonly lines: readonly IHeldOrderFireBlockLine[];
  /** `requiredChoicesLinesKey` of the pending lines the verdict was taken over. */
  readonly linesKey: string;
  /**
   * `requiredChoicesMenuKey`: a fingerprint of every menu document the rule
   * read for those lines. A verdict is valid only for its lines AND its menu.
   */
  readonly menuKey?: string;
  /**
   * One block EPISODE: opened when the order is first held back, closed when
   * the order leaves `scheduled` (the record is then removed). Line edits and
   * re-checks inside an episode keep it, and with it `blockedAt` and the
   * once-per-episode announcement.
   */
  readonly episodeId?: string;
  /** When the order was first held back in this episode. Never moves. */
  readonly blockedAt: Date;
  /**
   * When the server last recorded a verdict — a server timestamp taken at that
   * write. The judge NEVER touches the order's `updatedAt` (the till's sync
   * reconcile would then drop an offline edit made before the re-check), so the
   * order's `updatedAt` is stamped only by a till or API order write. The
   * functions activator compares the two: `updatedAt <= checkedAt` means no
   * order write since the verdict, which it can tell without a copy of the rule.
   */
  readonly checkedAt?: Date;
  /** The restaurant was told (set only after a successful send). */
  readonly announcedAt?: Date | null;
  /** A run has claimed the announcement (a lease, so a crashed send retries). */
  readonly announceClaimedAt?: Date | null;
}

/** "Tom Yum (size), Springroll (Springroll Sauce, Springroll Filling)" — for a log line or a notification body. */
export function describeHeldOrderFireBlock(block: Pick<IHeldOrderFireBlock, "lines">): string {
  return block.lines
    .map((line) => {
      const owed = [
        ...(line.owesSize ? ["size"] : []),
        ...line.unansweredGroups.map((g) => g.name),
      ];
      return owed.length > 0 ? `${line.name} (${owed.join(", ")})` : line.name;
    })
    .join(", ");
}

/**
 * Read a stored `fireBlocked` defensively: a malformed map reads as absent
 * rather than failing the whole order parse (the `taxExemption` rule).
 * `blockedAt` arrives as a Firestore Timestamp, an ISO string or a Date.
 */
export function parseHeldOrderFireBlock(raw: unknown): IHeldOrderFireBlock | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  if (r.reason !== HELD_ORDER_FIRE_BLOCK_REQUIRED_CHOICES) return undefined;
  if (typeof r.linesKey !== "string" || !Array.isArray(r.lines)) return undefined;
  const blockedAt = readDate(r.blockedAt);
  if (!blockedAt || Number.isNaN(blockedAt.getTime())) return undefined;
  const lines: IHeldOrderFireBlockLine[] = [];
  for (const l of r.lines as unknown[]) {
    if (!l || typeof l !== "object") continue;
    const line = l as Record<string, unknown>;
    lines.push({
      ...(typeof line.orderItemGuid === "string" ? { orderItemGuid: line.orderItemGuid } : {}),
      name: typeof line.name === "string" ? line.name : "",
      owesSize: line.owesSize === true,
      unansweredGroups: Array.isArray(line.unansweredGroups)
        ? (line.unansweredGroups as Array<Record<string, unknown>>)
            .filter((g) => g && typeof g.modifierGroupId === "number")
            .map((g) => ({
              modifierGroupId: g.modifierGroupId as number,
              name: typeof g.name === "string" ? g.name : "",
              minSelection: typeof g.minSelection === "number" ? g.minSelection : 1,
            }))
        : [],
    });
  }
  const checkedAt = readDate(r.checkedAt);
  const announcedAt = readDate(r.announcedAt);
  const announceClaimedAt = readDate(r.announceClaimedAt);
  return {
    reason: HELD_ORDER_FIRE_BLOCK_REQUIRED_CHOICES,
    lines,
    linesKey: r.linesKey,
    ...(typeof r.menuKey === "string" ? { menuKey: r.menuKey } : {}),
    ...(typeof r.episodeId === "string" ? { episodeId: r.episodeId } : {}),
    blockedAt,
    ...(checkedAt ? { checkedAt } : {}),
    announcedAt: announcedAt ?? null,
    announceClaimedAt: announceClaimedAt ?? null,
  };
}

/** A Firestore Timestamp, an ISO string, epoch millis or a Date — else undefined. */
function readDate(raw: unknown): Date | undefined {
  let d: Date | undefined;
  if (raw instanceof Date) d = raw;
  else if (raw && typeof raw === "object" && typeof (raw as { toDate?: unknown }).toDate === "function") {
    d = (raw as { toDate: () => Date }).toDate();
  } else if (typeof raw === "string" || typeof raw === "number") d = new Date(raw);
  return d && !Number.isNaN(d.getTime()) ? d : undefined;
}
